import { useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

interface GoogleSignInProps {
  onSuccess?: () => void;
}

export const useGoogleSignIn = ({ onSuccess }: GoogleSignInProps = {}) => {
  const { login } = useAuth();

  const handleGoogleLogin = useCallback(async (response: any) => {
    if (!response?.credential) {
      console.error('Invalid Google sign-in response');
      return;
    }

    try {
      const { data } = await axios.post('/api/google', {
        token: response.credential,
      });

      if (!data?.user || !data?.token) {
        throw new Error('Invalid response from server');
      }

      login(data.user, data.token);
      onSuccess?.();
    } catch (err: any) {
      console.error('Google sign-in error:', {
        message: err.response?.data?.error || err.message || 'An error occurred during Google sign-in',
        status: err.response?.status,
      });
    }
  }, [login, onSuccess]);

  useEffect(() => {
    const MAX_RETRIES = 5;
    let retryCount = 0;
    let retryTimeout: NodeJS.Timeout;

    const initializeGoogle = () => {
      if (!GOOGLE_CLIENT_ID) {
        console.error('Google Client ID is not configured');
        return;
      }

      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleLogin,
          });
        } catch (error) {
          console.error('Failed to initialize Google Sign-In:', error);
        }
      } else {
        retryCount++;
        if (retryCount <= MAX_RETRIES) {
          console.warn(`Attempting to initialize Google Sign-In (attempt ${retryCount}/${MAX_RETRIES})`);
          retryTimeout = setTimeout(initializeGoogle, 2000);
        } else {
          console.error('Failed to initialize Google Sign-In after maximum retries');
        }
      }
    };

    initializeGoogle();

    return () => {
      clearTimeout(retryTimeout);
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.cancel();
        } catch (error) {
          console.error('Error cleaning up Google Sign-In:', error);
        }
      }
    };
  }, [handleGoogleLogin]);

  const promptGoogleSignIn = () => {
    if (!GOOGLE_CLIENT_ID) {
      console.error('Google Client ID is not configured');
      return;
    }

    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    } else {
      console.error('Google Sign-In is not properly initialized');
    }
  };

  return { promptGoogleSignIn };
};
